import * as React from 'react';

import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';

const DEFAULT_MIN_ROWS = 2;
const DEFAULT_MAX_HEIGHT_PX = 320;

function setRef(ref, node) {
  if (typeof ref === 'function') ref(node);
  else if (ref) ref.current = node;
}

/**
 * Textarea that grows with content up to maxHeight, then scrolls internally.
 */
const AutoResizeTextarea = React.forwardRef(
  (
    {
      className,
      value,
      onChange,
      minRows = DEFAULT_MIN_ROWS,
      maxHeight = DEFAULT_MAX_HEIGHT_PX,
      ...props
    },
    forwardedRef
  ) => {
    const innerRef = React.useRef(null);

    const adjustHeight = React.useCallback(() => {
      const el = innerRef.current;
      if (!el) return;

      el.style.height = 'auto';
      const style = window.getComputedStyle(el);
      const lineHeight = parseFloat(style.lineHeight) || 20;
      const padding =
        parseFloat(style.paddingTop) +
        parseFloat(style.paddingBottom) +
        parseFloat(style.borderTopWidth) +
        parseFloat(style.borderBottomWidth);
      const minHeight = lineHeight * minRows + padding;
      const contentHeight = el.scrollHeight;
      const next = Math.min(Math.max(contentHeight, minHeight), maxHeight);

      el.style.height = `${next}px`;
      el.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden';
    }, [minRows, maxHeight]);

    React.useLayoutEffect(() => {
      adjustHeight();
    }, [value, adjustHeight, minRows, maxHeight]);

    React.useEffect(() => {
      window.addEventListener('resize', adjustHeight);
      return () => window.removeEventListener('resize', adjustHeight);
    }, [adjustHeight]);

    const handleRef = React.useCallback(
      (node) => {
        innerRef.current = node;
        setRef(forwardedRef, node);
      },
      [forwardedRef]
    );

    return (
      <Textarea
        ref={handleRef}
        value={value}
        onChange={onChange}
        onInput={adjustHeight}
        rows={minRows}
        className={cn('resize-none', className)}
        {...props}
      />
    );
  }
);

AutoResizeTextarea.displayName = 'AutoResizeTextarea';

export { AutoResizeTextarea };
